import { Activity, DanceRoles } from '../../../pure-js/types/GrooverTypes'
import { State } from '../hooks/useAppState'

/**
 * The role a couple's other half takes for a given role, or null when there is no complement.
 * Only Leader and Follower pair up: Both means "either", and Single means there is no partner —
 * deriving a partner role from those would write a phantom partner into shared registration state.
 */
export const getComplementaryDanceRole = (role: DanceRoles): DanceRoles | null => {
  if (role === DanceRoles.Leader) return DanceRoles.Follower
  if (role === DanceRoles.Follower) return DanceRoles.Leader
  return null
}

/** Leader / Follower / Both — the roles the step 2 radios can actually show. */
export const isSelectableDanceRole = (role?: DanceRoles | null): role is DanceRoles =>
  role === DanceRoles.Leader || role === DanceRoles.Follower || role === DanceRoles.Both

/** `Single` is a step-1 default for individual flow, not a visible Rollval choice. */
export const getSelectableDanceRole = (role?: DanceRoles | null): DanceRoles | null =>
  isSelectableDanceRole(role) ? role : null

/** Whether step 2 role UI and step 3 submit validation require an explicit dance role. */
export const isRoleSelectionRequired = (state: State, activity?: Activity): boolean => {
  const hasChildActivity = state.selectedActivities.some((a) => a.allowChildRegistration)
  const registrationOverrideMode = activity?.registrationOverride?.mode
  const registrationOverrideShowRoleWithValidation =
    activity?.registrationOverride?.individual?.showRoleWithValidation ?? true

  return (
    state.individualFlow &&
    !hasChildActivity &&
    state.selectedActivities.some((a) => !a.singleRegistration) &&
    !isSelectableDanceRole(state.obfuscatedEnrollment?.mainUser?.type) &&
    !(registrationOverrideMode === 'individual' && registrationOverrideShowRoleWithValidation === false)
  )
}

/** Resolves main user dance role for step 3 submit, defaulting to Single when role UI was skipped. */
export const resolveMainUserRoleForSubmit = (state: State, activity?: Activity): DanceRoles | undefined => {
  const explicitRole = state.obfuscatedEnrollment?.mainUser?.type || state.role
  if (isRoleSelectionRequired(state, activity)) {
    return getSelectableDanceRole(explicitRole) ?? undefined
  }
  if (explicitRole) return explicitRole

  if (state.selectedActivities.some((a) => a.allowChildRegistration)) {
    return DanceRoles.Single
  }

  const allSelectedAreSingleRegistration =
    state.selectedActivities.length > 0 && state.selectedActivities.every((a) => a.singleRegistration)

  // Parent events are often not singleRegistration themselves; step 2 hides role UI when every
  // *selected subevent* is singleRegistration. Default Single in individual flow only — couple flow
  // must use roles from step 1 (Leader/Follower), not Single.
  if (allSelectedAreSingleRegistration && state.individualFlow) {
    return DanceRoles.Single
  }

  if (!isRoleSelectionRequired(state, activity) && state.individualFlow && state.selectedActivities.length > 0) {
    return DanceRoles.Single
  }

  return undefined
}
